// AdminImportStudentDetails.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Chip,
  DialogActions,
  Dialog as MuiDialog,
  LinearProgress,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt'; // NEW: Move to Master icon
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import LoadingSkeleton from 'components/LoadingSkeleton';
import { adminStyles, loadingConfig } from 'styles/adminStyles';
import * as XLSX from 'xlsx';
import axiosInstance from 'API/axiosInstance';
import { API_ENDPOINTS } from 'config/api';
import AdminUploadedAlumniDataDialog from './AdminUploadedAlumniDataDialog';

const AdminImportStudentDetails = ({ open, onClose }) => {
  const [file, setFile] = useState(null);

  // parsing
  const [parsing, setParsing] = useState(false);
  const [sheetName, setSheetName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]); // full parsed rows as objects

  // actions
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isMovingToMaster, setIsMovingToMaster] = useState(false); // NEW
  const [confirmOpen, setConfirmOpen] = useState(false);

  // results
  const [uploadStatus, setUploadStatus] = useState(null); // 'success' | 'error' | null
  const [responseMessage, setResponseMessage] = useState('');
  const [importedRecords, setImportedRecords] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  // snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info'); // 'success' | 'error' | 'warning' | 'info'

  // duplicate detection
  const [duplicates, setDuplicates] = useState([]);

  // uploaded data dialog
  const [uploadedDataDialogOpen, setUploadedDataDialogOpen] = useState(false);
  const handleOpenUploadedDataDialog = () => setUploadedDataDialogOpen(true);
  const handleCloseUploadedDataDialog = () => setUploadedDataDialogOpen(false);

  const resetAll = () => {
    setFile(null);
    setParsing(false);
    setSheetName('');
    setHeaders([]);
    setRows([]);
    setIsDownloading(false);
    setIsUploading(false);
    setIsMovingToMaster(false);
    setConfirmOpen(false);
    setUploadStatus(null);
    setResponseMessage('');
    setImportedRecords(0);
    setErrorMessage('');
    setOpenSnackbar(false);
    setSnackbarMessage('');
    setSnackbarSeverity('info');
    setDuplicates([]);
  };

  const handleCloseDialog = () => {
    resetAll();
    onClose();
  };

  const humanSize = (nBytes) => {
    if (!nBytes && nBytes !== 0) return '';
    const units = ['Bytes', 'KB', 'MB', 'GB'];
    let i = 0;
    let size = nBytes;
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024;
      i += 1;
    }
    return `${size.toFixed(2)} ${units[i]}`;
  };

  const handleFileChange = async (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;

    setFile(selected);
    await parseExcel(selected);
  };

  const parseExcel = async (selectedFile) => {
    setParsing(true);
    setHeaders([]);
    setRows([]);
    setErrorMessage('');
    setDuplicates([]);
    try {
      const data = await selectedFile.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });

      const wsName = wb.SheetNames[0];
      const ws = wb.Sheets[wsName];

      // Convert to JSON with header row auto-detected
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });

      // Capture headers (from the first row keys)
      const hdrs = json.length ? Object.keys(json[0]) : [];

      setSheetName(wsName);
      setHeaders(hdrs);
      setRows(json);

      // Check for duplicates within the file
      const duplicateList = checkDuplicatesInFile(json);
      setDuplicates(duplicateList);
    } catch (err) {
      console.error('Excel parse error:', err);
      setErrorMessage('Failed to parse the Excel file. Please ensure it is a valid .xlsx or .xls file.');
    } finally {
      setParsing(false);
    }
  };

  const checkDuplicatesInFile = (data) => {
    const duplicates = [];
    const seenRecords = new Map(); // Key: Email|Contact|WhatsApp, Value: {rowIndex, name}

    data.forEach((row, index) => {
      const email = (row['Email ID'] || '').toString().trim().toLowerCase();
      const contact = (row['Contact Number'] || '').toString().trim();
      const whatsapp = (row['Whatsapp Number'] || '').toString().trim();
      const name = (row['Name'] || '').toString().trim();

      // Check for duplicates
      const keys = [];
      if (email) keys.push({ type: 'Email', value: email });
      if (contact) keys.push({ type: 'Contact', value: contact });
      if (whatsapp) keys.push({ type: 'WhatsApp', value: whatsapp });

      keys.forEach(({ type, value }) => {
        const key = `${type}:${value}`;
        if (seenRecords.has(key)) {
          const existing = seenRecords.get(key);
          duplicates.push({
            rowIndex: index + 1, // 1-based for display
            name: name || 'N/A',
            email: email || 'N/A',
            contact: contact || 'N/A',
            whatsapp: whatsapp || 'N/A',
            reason: `Duplicate ${type.toLowerCase()} found at row ${existing.rowIndex}`,
            duplicateWith: existing.rowIndex,
          });
        } else {
          seenRecords.set(key, { rowIndex: index + 1, name });
        }
      });
    });

    return duplicates;
  };

  const downloadTemplate = async () => {
    setIsDownloading(true);
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.ADMIN.DOWNLOAD_TEMPLATE, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'AlumniTemplate.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      setErrorMessage('Failed to download the template. Please try again later.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleAskConfirm = () => {
    if (!file || rows.length === 0) {
      setErrorMessage('Please upload a valid Excel file with at least one data row before importing.');
      return;
    }
    if (duplicates.length > 0) {
      setErrorMessage(`Found ${duplicates.length} duplicate record(s) in the file. Please remove duplicates before importing.`);
      setSnackbarMessage(`Found ${duplicates.length} duplicate record(s). Please review and remove them.`);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }
    setConfirmOpen(true);
  };

  const handleImport = async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadStatus(null);
    setErrorMessage('');
    setConfirmOpen(false);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axiosInstance.post(
        API_ENDPOINTS.ADMIN.UPLOAD_STUDENTS(1),
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      const msg = response?.data?.message ?? 'Imported successfully.';
      setResponseMessage(msg);

      // Extract counts from response
      const insertedCount = response?.data?.insertedCount ?? 0;
      const skippedCount = response?.data?.skippedCount ?? 0;
      setImportedRecords(insertedCount);

      if (skippedCount > 0) {
        setResponseMessage(`${msg} ${skippedCount} duplicate(s) were skipped.`);
      }

      setUploadStatus('success');
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorData = error.response?.data;
      
      // Handle duplicate error response
      if (errorData?.duplicates && errorData.duplicates.length > 0) {
        setErrorMessage(
          `Found ${errorData.duplicateCount} duplicate record(s) in the file. Please remove duplicates before importing.`
        );
        setDuplicates(errorData.duplicates);
      } else {
        setErrorMessage(errorData?.message || 'Failed to upload the file. Please try again.');
      }
      setUploadStatus('error');
    } finally {
      setIsUploading(false);
    }
  };

  // NEW: Move to Master handler - opens dialog instead
  const handleMoveToMaster = () => {
    handleOpenUploadedDataDialog();
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} fullScreen>
      {isUploading && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 3 }} />}

      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
        <Tooltip title="Close">
          <IconButton onClick={handleCloseDialog} color="error">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogTitle sx={adminStyles.dialogTitle}>
        Import Student Details
      </DialogTitle>

      <DialogContent sx={adminStyles.container}>
        <MDBox display="flex" flexDirection="column" gap={2}>

          {/* SUCCESS */}
          {uploadStatus === 'success' && (
            <Card sx={{ p: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 1 }} />
                <MDTypography variant="h5" gutterBottom>
                  Import Successful
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {responseMessage || 'Imported successfully.'}
                </MDTypography>
                <Chip label={`Records imported: ${importedRecords}`} color="success" variant="outlined" sx={{ mb: 2 }} />
                <Box>
                  <MDButton color="primary" onClick={resetAll}>
                    Import Another File
                  </MDButton>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* ERROR */}
          {uploadStatus === 'error' && (
            <Card sx={{ p: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <ErrorIcon color="error" sx={{ fontSize: 64, mb: 1 }} />
                <MDTypography variant="h5" gutterBottom>
                  Import Failed
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {errorMessage || 'There was an error importing the file.'}
                </MDTypography>
                <Box>
                  <MDButton color="primary" onClick={resetAll}>
                    Try Again
                  </MDButton>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* MAIN CONTENT (when not finished) */}
          {!uploadStatus && (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                <MDTypography variant="h6" fontWeight="medium">
                  Student Data Import
                </MDTypography>

                {/* Right side buttons: Download template + Move to Master */}
                <Box display="flex" gap={1} alignItems="center">
                  <MDButton
                    variant="outlined"
                    color="info"
                    startIcon={<DownloadIcon />}
                    onClick={downloadTemplate}
                    disabled={isDownloading}
                  >
                    {isDownloading ? 'Downloading…' : 'Download Template'}
                  </MDButton>

                  <Tooltip title="Move parsed/imported data to master tables">
                    <span>
                      <MDButton
                        variant="outlined"
                        color="primary"
                        startIcon={
                          isMovingToMaster ? <CircularProgress size={18} color="inherit" /> : <SyncAltIcon />
                        }
                        onClick={handleMoveToMaster}
                        disabled={isMovingToMaster}
                      >
                        {isMovingToMaster ? 'Moving…' : 'Move to Master'}
                      </MDButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>

              <Alert icon={<InfoOutlinedIcon />} severity="info">
                Download the template, fill in the details, upload the Excel file, review the summary, and then confirm to import.
              </Alert>

              {/* Upload Card */}
              <Card
                variant="outlined"
                sx={{
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  borderColor: file ? 'success.main' : 'divider',
                  bgcolor: '#fff',
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 5 }}>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    id="excel-file-input"
                  />
                  <UploadFileIcon sx={{ fontSize: 54, mb: 1, color: 'primary.main' }} />
                  <MDTypography variant="h6" gutterBottom>
                    {file ? file.name : 'Upload Excel File'}
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {file
                      ? `${humanSize(file.size)} • Last modified: ${new Date(file.lastModified).toLocaleDateString()}`
                      : 'Drag & drop an Excel file here, or click to browse'}
                  </MDTypography>
                  <label htmlFor="excel-file-input">
                    <MDButton
                      variant="contained"
                      component="span"
                      color="info"
                      startIcon={<UploadFileIcon />}
                      disabled={parsing}
                    >
                      {file ? 'Change File' : 'Select File'}
                    </MDButton>
                  </label>
                  {parsing && (
                    <Box mt={2}>
                      <LoadingSkeleton variant="card" rows={1} showHeader={false} />
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Duplicate Warning */}
              {duplicates.length > 0 && !isUploading && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <MDTypography variant="body2" fontWeight="medium" gutterBottom>
                    Found {duplicates.length} duplicate record(s) in the file:
                  </MDTypography>
                  <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
                    {duplicates.slice(0, 5).map((dup, idx) => (
                      <li key={idx}>
                        <MDTypography variant="caption">
                          Row {dup.rowIndex}: {dup.name} - {dup.reason}
                        </MDTypography>
                      </li>
                    ))}
                    {duplicates.length > 5 && (
                      <li>
                        <MDTypography variant="caption">
                          ... and {duplicates.length - 5} more
                        </MDTypography>
                      </li>
                    )}
                  </Box>
                </Alert>
              )}

              {errorMessage && (
                <Alert severity="error">
                  {errorMessage}
                </Alert>
              )}

              <Box display="flex" gap={2} mt="auto" justifyContent="flex-end">
                <MDButton variant="outlined" color="secondary" onClick={handleCloseDialog}>
                  Cancel
                </MDButton>
                <MDButton
                  variant="contained"
                  color="primary"
                  onClick={handleAskConfirm}
                  disabled={!file || parsing || rows.length === 0 || isUploading || duplicates.length > 0}
                  startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  {isUploading ? 'Importing…' : duplicates.length > 0 ? 'Fix Duplicates First' : 'Confirm & Import'}
                </MDButton>
              </Box>
            </>
          )}
        </MDBox>
      </DialogContent>

      {/* Confirmation Dialog */}
      <MuiDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Import</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text.secondary">
            Do you want to import these records? Detected rows: {rows.length}. Please ensure the columns match your template.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={() => setConfirmOpen(false)}>
            No
          </MDButton>
          <MDButton
            variant="contained"
            color="primary"
            onClick={handleImport}
            autoFocus
            startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : null}
            disabled={isUploading}
          >
            {isUploading ? 'Importing…' : 'Yes, Import'}
          </MDButton>
        </DialogActions>
      </MuiDialog>

      {/* Snackbar for Move-to-Master (and could reuse for other notices) */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Uploaded Alumni Data Dialog */}
      <AdminUploadedAlumniDataDialog
        open={uploadedDataDialogOpen}
        onClose={handleCloseUploadedDataDialog}
        uploadedBy={1} // TODO: Get from auth context
      />
    </Dialog>
  );
};

AdminImportStudentDetails.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminImportStudentDetails;
